import * as React from 'react'
import {Dispatch, FC, SetStateAction} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {PaymentCurrency} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {WidgetEventName} from '../../../../lib/WidgetEventer'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {InfoToggle} from '../../../ui/InfoToggle'
import {InputRadioWrapper} from '../../../wrappers/InputCheckableWrapper'
import {DirtyWrapper} from '../../../wrappers/DirtyWrapper'

///////////////////////////////////////////////////////////////////////////////

export type PaymentCurrenciesProps = {
  setCurrenciesListShown: Dispatch<SetStateAction<boolean>>
}

///////////////////////////////////////////////////////////////////////////////

export const PaymentCurrencies: FC<PaymentCurrenciesProps> = observer(
  (props) => {
    const {setCurrenciesListShown} = props
    const {api} = useStores<{api: WidgetApi}>()
    const form = api.paymentForm
    const currencies = form.getPossibleCurrencies()
    const currentCurrency = form.getCurrency()
    const {uiTransitionTimeout: timeout} = api.computedConfig
    const infoToggleTitle = api.trans('info_toggle.currencies.title', {
      defaultValue: null,
    })
    const infoToggleTextHTML = api.trans('info_toggle.currencies.text_html', {
      defaultValue: null,
    })

    const setCurrency = (currency: PaymentCurrency) => {
      setTimeout(async () => {
        form.setCurrency(currency)
        await api.events[WidgetEventName.CURRENCY_CHANGED].publish({api})
      })
    }

    const onMouseUp = () => {
      setTimeout(() => {
        setCurrenciesListShown(false)
      }, timeout)
    }

    return (
      <div className="payment-currencies">
        <DynamicSlot name={DynamicSlotName.CURRENCIES_START} form={form} />

        <div className="header">
          {api.trans(
            'blocks.payment_amounts_and_intervals.currencies.select_currency',
          )}
        </div>

        <div className="main">
          <fieldset className="main currencies">
            <legend>
              {api.trans(
                'blocks.payment_amounts_and_intervals.currencies.legend',
              )}
            </legend>

            {currencies.map((currency, key) => {
              const isChecked = currency === currentCurrency

              return (
                <label
                  key={key}
                  onMouseUp={onMouseUp}
                  className={cn([
                    'label-radio-block',
                    'currency',
                    {selected: isChecked},
                  ])}
                >
                  <div className="label-radio-block-inner">
                    <InputRadioWrapper>
                      <DirtyWrapper form={form} fields="currency">
                        <input
                          type="radio"
                          aria-describedby={api.ui.generateUniqueElementId(
                            'currency-error',
                          )}
                          className="input-radio"
                          name="currency"
                          value={currency}
                          onChange={() => setCurrency(currency)}
                          checked={isChecked}
                        />
                      </DirtyWrapper>
                    </InputRadioWrapper>
                    <span className="code">{currency.toUpperCase()}</span>
                    <span className="divider">-</span>
                    <span className="value">
                      {api.trans(`currencies.${currency}`)}
                    </span>
                  </div>
                </label>
              )
            })}
          </fieldset>

          <InfoToggle
            idKey="info-toggle-currencies"
            className="info-toggle-currencies"
            title={infoToggleTitle}
            textHTML={infoToggleTextHTML}
          />

          <DynamicSlot name={DynamicSlotName.CURRENCIES_END} form={form} />
        </div>
      </div>
    )
  },
)
